import { Controller } from "@hotwired/stimulus"

// パートナーのインクリメンタルサーチ。
// 入力で候補を取得し、クリックまたは方向キー（↑↓）＋Enterで選択、Escで閉じる。
export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput"]
  static values = { url: String }

  connect() {
    this.activeIndex = -1
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)
    this.hideDropdown()
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  async search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 1) {
      this.hideDropdown()
      this.hiddenInputTarget.value = ""
      return
    }

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`)
      const partners = await response.json()

      this.renderDropdown(partners)
      this.showDropdown()
    } catch (error) {
      console.error("Partner search error:", error)
      this.hideDropdown()
    }
  }

  renderDropdown(partners) {
    this.activeIndex = -1

    if (partners.length === 0) {
      this.dropdownTarget.innerHTML = `
        <div class="px-3 py-2 text-sm text-gray-500">
          該当するパートナーが見つかりません
        </div>
      `
      return
    }

    this.dropdownTarget.innerHTML = partners.map((partner, index) => `
      <button type="button"
              role="option"
              aria-selected="false"
              id="partner-option-${index}"
              class="partner-option w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:outline-none"
              data-action="click->partner-search#selectPartner mouseover->partner-search#hover"
              data-index="${index}"
              data-partner-id="${partner.id}"
              data-partner-name="${partner.text}"
              data-partner-email="${partner.email}">
        <div class="font-medium text-gray-900">${partner.text}</div>
        <div class="text-xs text-gray-500">${partner.email}</div>
      </button>
    `).join("")
  }

  // 入力欄でのキー操作（↑↓で候補移動、Enterで確定、Escで閉じる）
  keydown(event) {
    const options = this.optionElements

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.dropdownHidden) {
          this.search()
        } else if (options.length > 0) {
          this.setActive(this.activeIndex + 1 >= options.length ? 0 : this.activeIndex + 1)
        }
        break
      case "ArrowUp":
        if (options.length === 0) return
        event.preventDefault()
        this.setActive(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1)
        break
      case "Enter":
        if (!this.dropdownHidden && this.activeIndex >= 0 && options[this.activeIndex]) {
          event.preventDefault()
          this.choose(options[this.activeIndex])
        }
        break
      case "Escape":
        if (!this.dropdownHidden) {
          event.preventDefault()
          this.hideDropdown()
        }
        break
    }
  }

  hover(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (!Number.isNaN(index)) this.setActive(index)
  }

  setActive(index) {
    const options = this.optionElements
    this.activeIndex = index

    options.forEach((el, i) => {
      const active = i === index
      el.classList.toggle("bg-gray-100", active)
      el.setAttribute("aria-selected", active ? "true" : "false")
    })

    const current = options[index]
    if (current) {
      current.scrollIntoView({ block: "nearest" })
      this.inputTarget.setAttribute("aria-activedescendant", current.id)
    }
  }

  selectPartner(event) {
    this.choose(event.currentTarget)
  }

  choose(optionEl) {
    this.inputTarget.value = optionEl.dataset.partnerName
    this.hiddenInputTarget.value = optionEl.dataset.partnerId
    this.hideDropdown()
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.activeIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenInputTarget.value = ""
    this.hideDropdown()
  }

  get optionElements() {
    return Array.from(this.dropdownTarget.querySelectorAll(".partner-option"))
  }

  get dropdownHidden() {
    return this.dropdownTarget.classList.contains("hidden")
  }
}
